import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc, limit } from 'firebase/firestore';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation'; // Import Breadcrumb
import { Container, Row, Col, Button, Card, Badge } from 'react-bootstrap'; // Import Bootstrap components
import { createLogger } from '../../utils/logger';

const logger = createLogger('EmployeeDashboard');

export default function EmployeeDashboard() {
  const { currentUser, userRole, employeeSupermarketId } = useAuth(); 
  const navigate = useNavigate();
  const [ordersToPack, setOrdersToPack] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeSupermarketName, setEmployeeSupermarketName] = useState('');
  const [showFinancialData, setShowFinancialData] = useState(true);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalAwaitingPacking: 0,
    totalPacked: 0,
    totalCompleted: 0,
    fullPaymentCount: 0,
    confirmationFeeOnlyCount: 0,
    confirmationFeeTotal: 0,
    fullPaymentTotal: 0,
    fullPaymentMinusConfirmationFee: 0,
    totalOrders: 0
  });

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: "Employee Hub" } 
  ];

  useEffect(() => {
    if (userRole === 'Employee' && employeeSupermarketId) {
      const supermarketDocRef = doc(db, "Supermarkets", employeeSupermarketId);
      getDoc(supermarketDocRef).then(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setEmployeeSupermarketName(data.display_name || data.name || employeeSupermarketId);
          setShowFinancialData(data.show_financial_data !== undefined ? data.show_financial_data : true);
        } else {
          setEmployeeSupermarketName(employeeSupermarketId);
          setShowFinancialData(true);
        }
      }).catch(_err => {
        setEmployeeSupermarketName(employeeSupermarketId);
        setShowFinancialData(true);
      });
    } else if (userRole === 'Admin') {
      // Admins always see financial data
      setShowFinancialData(true);
    }
  }, [userRole, employeeSupermarketId]);

  // Helper function to determine if an order is a full payment
  const isFullPaymentOrder = useCallback((order) => {
    if (order.paymentType === 'full') {
      return true;
    }
    
    const orderSubtotal = order.orderSubtotal || 0;
    const confirmationFeeAmount = order.confirmationFee || parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
    const deliveryFeeAmount = order.deliveryOption === 'delivery' ? (Number(order.deliveryFee) || 0) : 0;
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount + deliveryFeeAmount;
    const amountPaid = parseFloat(order.amountPaid) || parseFloat(order.total) || 0;
    
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  }, []);

  // Fetch summary statistics
  const fetchSummaryStats = useCallback(async () => {
    try {
      const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
      
      logger.debug('[EmployeeDashboard] Fetching summary stats...');
      logger.debug('[EmployeeDashboard] User role:', userRole);
      logger.debug('[EmployeeDashboard] Employee supermarket ID:', employeeSupermarketId);
      
      // Query for all transactions (for summary stats)
      // Try without orderBy first to avoid index issues, then sort in memory
      let allTransactionsQuery = query(
        collection(db, 'Transactions')
      );
      
      if (userRole === 'Employee' && employeeSupermarketId) {
        allTransactionsQuery = query(allTransactionsQuery, where('supermarketId', '==', employeeSupermarketId));
        logger.debug('[EmployeeDashboard] Added supermarket filter for summary stats:', employeeSupermarketId);
      }
      
      let allSnapshot;
      try {
        allSnapshot = await getDocs(allTransactionsQuery);
      } catch (queryError) {
        // If query fails (e.g., missing index), try without orderBy
        logger.warn('[EmployeeDashboard] Query with orderBy failed, trying without orderBy:', queryError);
        allTransactionsQuery = query(collection(db, 'Transactions'));
        if (userRole === 'Employee' && employeeSupermarketId) {
          allTransactionsQuery = query(allTransactionsQuery, where('supermarketId', '==', employeeSupermarketId));
        }
        allSnapshot = await getDocs(allTransactionsQuery);
      }
      
      const allTransactions = allSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort by createdAt in memory if needed
      allTransactions.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      
      logger.debug('[EmployeeDashboard] Found', allTransactions.length, 'transactions for summary stats');
      
      // Debug: Log status distribution
      const statusCounts = {};
      allTransactions.forEach(order => {
        const status = order.status || 'undefined';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      logger.debug('[EmployeeDashboard] Status distribution:', statusCounts);
      
      // Calculate statistics
      const stats = {
        totalAwaitingPacking: 0,
        totalPacked: 0,
        totalCompleted: 0,
        fullPaymentCount: 0,
        confirmationFeeOnlyCount: 0,
        confirmationFeeTotal: 0,
        fullPaymentTotal: 0,
        fullPaymentMinusConfirmationFee: 0,
        totalOrders: allTransactions.length
      };
      
      allTransactions.forEach(order => {
        // Count by status
        if (order.status === 'awaiting_packing') {
          stats.totalAwaitingPacking++;
          logger.debug('[EmployeeDashboard] Found awaiting_packing order:', order.id);
        }
        if (order.status === 'packed_awaiting_pickup') stats.totalPacked++;
        if (order.status === 'completed' || order.status === 'picked_up') stats.totalCompleted++;
        
        // Exclude cancelled and active orders for financial calculations
        const isActive = order.status !== 'cancelled_by_employee' && order.status !== 'active';
        
        if (isActive) {
          const isFullPayment = isFullPaymentOrder(order);
          
          if (isFullPayment) {
            stats.fullPaymentCount++;
            const orderSubtotal = parseFloat(order.orderSubtotal) || 0;
            const confirmationFeeAmount = parseFloat(order.confirmationFee) || confirmationFee;
            stats.fullPaymentTotal += orderSubtotal + confirmationFeeAmount;
            stats.fullPaymentMinusConfirmationFee += orderSubtotal;
            stats.confirmationFeeTotal += confirmationFeeAmount; // Include fee portion
          } else {
            stats.confirmationFeeOnlyCount++;
            stats.confirmationFeeTotal += confirmationFee; // Only confirmation fee
          }
        }
      });
      
      logger.debug('[EmployeeDashboard] Summary stats calculated:', stats);
      setSummaryStats(stats);
    } catch (err) {
      logger.error('[EmployeeDashboard] Error fetching summary stats:', err);
      // Set error state so user knows something went wrong
      setError(`Failed to load dashboard statistics: ${err.message}`);
    }
  }, [userRole, employeeSupermarketId, isFullPaymentOrder]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      logger.debug('[EmployeeDashboard] Starting to fetch orders...');
      logger.debug('[EmployeeDashboard] User role:', userRole);
      logger.debug('[EmployeeDashboard] Employee supermarket ID:', employeeSupermarketId);
      
      // Query for orders awaiting packing only (to avoid duplicates)
      let ordersQuery = query(
        collection(db, 'Transactions'),
        where('status', '==', 'awaiting_packing'),
        orderBy('createdAt', 'asc')
      );
      if (userRole === 'Employee') {
        if (employeeSupermarketId) {
          ordersQuery = query(ordersQuery, where('supermarketId', '==', employeeSupermarketId));
          logger.debug('[EmployeeDashboard] Added supermarket filter for Employee:', employeeSupermarketId);
        } else {
          logger.warn('[EmployeeDashboard] Employee has no supermarket assigned');
          setOrdersToPack([]); setLoading(false); return;
        }
      } else {
        logger.debug('[EmployeeDashboard] Admin user - fetching all orders');
      }
      
      logger.debug('[EmployeeDashboard] Executing query...');
      const querySnapshot = await getDocs(ordersQuery);
      logger.debug('[EmployeeDashboard] Query completed. Found', querySnapshot.docs.length, 'documents');
      
      // If no orders found, let's check if there are any transactions at all
      if (querySnapshot.docs.length === 0) {
        logger.warn('[EmployeeDashboard] No orders found with status awaiting_packing. Checking all transactions...');
        const allTransactionsQuery = query(
          collection(db, 'Transactions'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const allSnapshot = await getDocs(allTransactionsQuery);
        logger.debug('[EmployeeDashboard] Found', allSnapshot.docs.length, 'total transactions');
        allSnapshot.docs.forEach(doc => {
          const data = doc.data();
          logger.debug('[EmployeeDashboard] Transaction:', { 
            id: doc.id, 
            status: data.status, 
            supermarketId: data.supermarketId,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || 'no date'
          });
        });
      }
      
      const fetchedOrders = querySnapshot.docs.map(doc => {
        const data = doc.data();
        logger.debug('[EmployeeDashboard] Order found:', { id: doc.id, status: data.status, supermarketId: data.supermarketId });
        return { id: doc.id, ...data };
      });
      
      // Fetch supermarket names and customer names for orders
      const ordersWithDetails = await Promise.all(
        fetchedOrders.map(async (order) => {
          const updatedOrder = { ...order };
          
          // Fetch supermarket name if not already available
          if (!order.supermarketName || order.supermarketName === 'N/A' || order.supermarketName === 'Unknown Supermarket') {
            if (order.supermarketId) {
              try {
                const supermarketDoc = await getDoc(doc(db, 'Supermarkets', order.supermarketId));
                if (supermarketDoc.exists()) {
                  const supermarketData = supermarketDoc.data();
                  updatedOrder.supermarketName = supermarketData.display_name || supermarketData.name || 'Unknown Supermarket';
                }
              } catch (error) {
                // Failed to fetch supermarket details
              }
            }
          }
          
          // Fetch customer name from Users collection
          // order.userId is the Firebase Auth UID, so we need to query by identity_id field
          if (order.userId) {
            try {
              logger.debug(`[EmployeeDashboard] Fetching customer name for userId: ${order.userId}`);
              
              // First try to find user by identity_id field
              const usersQuery = query(
                collection(db, 'Users'),
                where('identity_id', '==', order.userId),
                limit(1)
              );
              const userSnapshot = await getDocs(usersQuery);
              
              if (!userSnapshot.empty) {
                const userData = userSnapshot.docs[0].data();
                updatedOrder.customerName = userData.first_name || 'Customer';
                logger.debug(`[EmployeeDashboard] Found user by identity_id: ${updatedOrder.customerName} for userId: ${order.userId}`);
              } else {
                // If not found by identity_id, try to find by document ID (in case userId is the document ID)
                logger.debug(`[EmployeeDashboard] User not found by identity_id, trying document ID: ${order.userId}`);
                const userDoc = await getDoc(doc(db, 'Users', order.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  updatedOrder.customerName = userData.first_name || 'Customer';
                  logger.debug(`[EmployeeDashboard] Found user by document ID: ${updatedOrder.customerName} for userId: ${order.userId}`);
                } else {
                  logger.warn(`[EmployeeDashboard] User not found for userId: ${order.userId} (tried both identity_id and document ID)`);
                  updatedOrder.customerName = 'Customer';
                }
              }
            } catch (error) {
              logger.error(`[EmployeeDashboard] Error fetching user data for order ${order.id}:`, error);
              updatedOrder.customerName = 'Customer';
            }
          } else {
            logger.warn(`[EmployeeDashboard] Order ${order.id} missing userId`);
            updatedOrder.customerName = 'Customer';
          }
          
          return updatedOrder;
        })
      );
      
      setOrdersToPack(ordersWithDetails);
    } catch (err) {
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userRole, employeeSupermarketId]);

  // Restrict access: Only Admins and Employees can view this page
  useEffect(() => {
    if (!currentUser) {
      setError("Please log in to view this page."); setLoading(false); return;
    }
    if (userRole !== 'Admin' && userRole !== 'Employee') {
      setError("You are not authorized to view this page. Requires Admin or Employee role."); setLoading(false); return;
    }
    fetchOrders();
    fetchSummaryStats();
  }, [currentUser, userRole, fetchOrders, fetchSummaryStats]);

  const handlePackOrder = (transactionId) => navigate(`/employee/pack-order/${transactionId}`);

  if (loading && ordersToPack.length === 0) { 
    return (
      <Layout>
        <Container className="mt-4 px-3 text-center mb-5">
          <p>Loading packing queue...</p>
        </Container>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout>
        <Container className="mt-4 px-3 mb-5">
          <p className="text-danger">{error}</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="mt-4 px-3 mb-5">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3 gap-3">
            <h2 className="mb-0">Employee Dashboard</h2>
            <div className="d-flex flex-column flex-sm-row gap-2">
                {(userRole === 'Admin' || userRole === 'Employee') && (
                    <Link to="/inventory" className="btn btn-success">
                        Manage Inventory
                    </Link>
                )}
                {(userRole === 'Admin' || userRole === 'Employee') && (
                    <Link to="/employee/manage-packed-orders" className="btn btn-info">
                        Manage Packed Orders
                    </Link>
                )}
                {(userRole === 'Admin' || userRole === 'Employee') && (
                    <Link to="/admin/products" className="btn btn-outline-primary">
                        View product catalog
                    </Link>
                )}
            </div>
        </div>

        <div className="d-flex justify-content-end mb-3">
          <Link
            to={
              userRole === 'Employee' && employeeSupermarketId
                ? `/admin/reports?supermarket=${encodeURIComponent(employeeSupermarketId)}`
                : "/admin/reports"
            }
            className="text-decoration-underline fw-semibold"
            style={{
              color: '#ff8800',
              fontSize: '1.1em'
            }}
          >
            Go to Reports
          </Link>
        </div>

        {/* Dashboard Summary Section */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Dashboard Summary
            </h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {/* Order Status Cards */}
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-primary">
                  <Card.Body className="text-center">
                    <div className="text-primary mb-2">
                      <i className="fas fa-box-open fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalAwaitingPacking}</h3>
                    <p className="text-muted mb-0 small">Awaiting Packing</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-info">
                  <Card.Body className="text-center">
                    <div className="text-info mb-2">
                      <i className="fas fa-check-circle fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalPacked}</h3>
                    <p className="text-muted mb-0 small">Packed & Ready</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-success">
                  <Card.Body className="text-center">
                    <div className="text-success mb-2">
                      <i className="fas fa-clipboard-check fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalCompleted}</h3>
                    <p className="text-muted mb-0 small">Completed</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-secondary">
                  <Card.Body className="text-center">
                    <div className="text-secondary mb-2">
                      <i className="fas fa-shopping-cart fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalOrders}</h3>
                    <p className="text-muted mb-0 small">Total Orders</p>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Payment Type Cards - Only shown if showFinancialData is true */}
              {showFinancialData && (
                <>
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-success">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Full Payment Orders</span>
                          <Badge bg="success">{summaryStats.fullPaymentCount}</Badge>
                        </div>
                        <div className="text-success">
                          <strong>${Number(summaryStats.fullPaymentTotal).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          Subtotal: ${Number(summaryStats.fullPaymentMinusConfirmationFee).toFixed(2)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-secondary">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Confirmation Fee Only</span>
                          <Badge bg="secondary">{summaryStats.confirmationFeeOnlyCount}</Badge>
                        </div>
                        <div className="text-dark">
                          <strong>${Number(summaryStats.confirmationFeeTotal).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          Fees collected
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-warning">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Total Revenue</span>
                          <i className="fas fa-dollar-sign text-warning"></i>
                        </div>
                        <div className="text-warning">
                          <strong>${(Number(summaryStats.fullPaymentTotal) + Number(summaryStats.confirmationFeeTotal)).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          All payments collected
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>

        <h4 className="mb-3 text-break">Orders Awaiting Packing 
          {userRole === 'Admin' ? ' (Admin View - All Supermarkets)' : 
           (userRole === 'Employee' && employeeSupermarketId ? (employeeSupermarketName ? ` for ${employeeSupermarketName}`: '') : ' (No Supermarket Assigned)')}
        </h4>
        
        {ordersToPack.length === 0 ? (
          <p className="text-center text-md-start">
            No orders are currently awaiting packing 
            {userRole === 'Employee' && employeeSupermarketId ? 
              ` for your assigned supermarket (${employeeSupermarketName || employeeSupermarketId})` : 
             (userRole === 'Employee' ? ' (No supermarket assigned to your employee profile)':'.')}
          </p>
        ) : (
          <div className="row g-3 mb-5">
            {ordersToPack.map(order => (
              <div key={order.id} className="col-12 col-md-6 col-lg-4 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-header bg-light border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0 fw-bold text-dark">
                          <span 
                            title={`Full Order ID: ${order.id} - Click to copy`}
                            style={{ cursor: 'pointer' }}
                            onClick={async () => {
                              try {
                                if (navigator.clipboard) {
                                  await navigator.clipboard.writeText(order.id);
                                  setCopiedOrderId(order.id);
                                  setTimeout(() => setCopiedOrderId(null), 2000);
                                }
                              } catch (_err) {
                                // Failed to copy
                              }
                            }}
                            className={`text-decoration-underline ${copiedOrderId === order.id ? 'text-success' : ''}`}
                          >
                            <span className="d-block d-lg-none">
                              Order #{order.id.substring(0, 8)}...
                            </span>
                            <span className="d-none d-lg-block">
                              Order #{order.id}
                            </span>
                            {copiedOrderId === order.id && (
                              <i className="fas fa-check ms-1 text-success"></i>
                            )}
                          </span>
                        </h6>
                        <div className="mb-1">
                          <small className="text-muted">
                            Cart: {order.cartInstanceId ? order.cartInstanceId.replace('cart_instance_', '') : 'N/A'}
                          </small>
                        </div>
                        <small className="text-muted">
                          {order.createdAt?.toDate().toLocaleDateString()}
                        </small>
                      </div>
                      <div className="text-end">
                        <span className="badge bg-primary fs-6 px-3 py-2">
                          <i className="fas fa-boxes me-1"></i>
                          {Array.isArray(order.cartItems) ? order.cartItems.length : 0} Items
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                      {/* Payment Type Indicator */}
                      {isFullPaymentOrder(order) && (
                        <div className="mb-2">
                          <span className="badge bg-success text-white">
                            <i className="fas fa-check-circle me-1"></i>
                            Full Payment - Paid in Full
                          </span>
                        </div>
                      )}
                      
                      {/* Customer Name */}
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-user me-2 text-muted"></i>
                        <span className="fw-semibold text-break">
                          {order.customerName && order.customerName !== 'Simulated Customer' ? order.customerName : 'Customer'}
                        </span>
                      </div>
                      
                      {/* Supermarket */}
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-store me-2 text-muted"></i>
                        <span className="fw-semibold text-break">
                          {order.supermarketName || 'Unknown Supermarket'}
                        </span>
                      </div>
                      
                      {/* Delivery Indicator */}
                      {order.deliveryOption === 'delivery' && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <span className="badge bg-info text-white me-2">
                              <i className="fas fa-truck me-1"></i>
                              Delivery Order
                            </span>
                          </div>
                          {order.deliveryAddress && (
                            <div className="small text-muted mb-1">
                              <i className="fas fa-map-marker-alt me-1"></i>
                              <strong>Address:</strong> {order.deliveryAddress}
                            </div>
                          )}
                          {order.deliveryPhone && (
                            <div className="small text-muted">
                              <i className="fas fa-phone me-1"></i>
                              <strong>Phone:</strong> {order.deliveryPhone}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-auto">
                      <Button 
                        variant="success" 
                        size="lg" 
                        className="w-100" 
                        onClick={() => handlePackOrder(order.id)}
                      >
                        <i className="fas fa-box me-2"></i>
                        Pack This Order
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </Layout>
  );
}
